/**
 * K-LyDapp · Fase 5A: Gestor Global de Pila de Ventanas Móviles (Modal Stack)
 * 
 * Reglas de Arquitectura:
 * 1. Pila unificada para KlSheet, KlFullModal y KlDialog.
 * 2. Bloqueo de scroll del body en iOS sin saltos (guardando y restaurando scrollY).
 * 3. Prioridad de cierre en botón Atrás de Android:
 *    1) Panel emergente
 *    2) Ventana superior de la pila (LIFO)
 *    3) Menú lateral
 *    4) Navegación atrás
 * 4. Cambio de ruta cierra automáticamente toda la pila.
 * 5. Restauración automática del foco al cerrar la ventana.
 */

export type ModalType = 'sheet' | 'full' | 'dialog' | 'menu';

export interface ModalEntry {
    id: string;
    type: ModalType;
    onClose: () => void;
    returnFocusElement?: HTMLElement | null;
}

export const MODAL_TIMINGS = {
    sheet: {
        openVar: 'var(--kl-dur-sheet-open, 300ms)',
        closeVar: 'var(--kl-dur-sheet-close, 220ms)',
        openMs: 300,
        closeMs: 220,
    },
    full: {
        openVar: 'var(--kl-dur-open, 280ms)',
        closeVar: 'var(--kl-dur-close, 200ms)',
        openMs: 280,
        closeMs: 200,
    },
    dialog: {
        fadeVar: 'var(--kl-dur-fade, 180ms)',
        fadeMs: 180,
    },
    reducedMotion: {
        fadeVar: '120ms',
        fadeMs: 120,
    },
    easeOut: 'var(--kl-ease-out, cubic-bezier(.32, .72, 0, 1))',
    easeIn: 'var(--kl-ease-in, cubic-bezier(.4, 0, 1, 1))',
    safetyMarginMs: 50
} as const;

let stack: ModalEntry[] = [];
const listeners = new Set<() => void>();

let savedScrollY = 0;
let isBodyScrollLocked = false;

interface SavedContainerState {
    savedScrollTop: number;
    originalOverflowY: string;
    originalTouchAction: string;
}

const lockedContainersMap = new Map<HTMLElement, SavedContainerState>();

function lockBodyScroll() {
    if (isBodyScrollLocked || typeof document === 'undefined') return;
    savedScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    isBodyScrollLocked = true;

    lockedContainersMap.clear();

    const isInsideModal = (el: HTMLElement): boolean => {
        return el.closest(
            '[data-kl-sheet="true"], [data-kl-fullmodal="true"], [data-kl-dialog="true"], [data-kl-menu="true"], #kl-modal-root, [role="dialog"]'
        ) !== null;
    };

    const isVisible = (el: HTMLElement): boolean => {
        return el.offsetParent !== null && window.getComputedStyle(el).display !== 'none';
    };

    // Bloquear solo contenedores fuera de la capa de ventanas y visibles
    const candidates = document.querySelectorAll<HTMLElement>(
        '.overflow-y-auto, .overflow-auto, [data-mobile-scroll="true"]'
    );

    candidates.forEach(el => {
        if (!isInsideModal(el) && isVisible(el)) {
            lockedContainersMap.set(el, {
                savedScrollTop: el.scrollTop,
                originalOverflowY: el.style.overflowY,
                originalTouchAction: el.style.touchAction
            });
            el.style.overflowY = 'hidden';
            el.style.touchAction = 'none';
        }
    });
}

function unlockBodyScroll() {
    if (!isBodyScrollLocked || typeof document === 'undefined') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, savedScrollY);
    isBodyScrollLocked = false;

    // Restaurar cada elemento directamente desde el Map
    lockedContainersMap.forEach((saved, el) => {
        el.style.overflowY = saved.originalOverflowY;
        el.style.touchAction = saved.originalTouchAction;
        el.scrollTop = saved.savedScrollTop;
    });
    lockedContainersMap.clear();
}

function notifyListeners() {
    listeners.forEach((fn) => {
        try {
            fn();
        } catch (err) {
            console.error('[modalStack] Error in listener:', err);
        }
    });
}

export const modalStack = {
    /**
     * Registra una ventana en la pila (al abrir)
     * Retorna una función de desregistro para cleanup
     */
    push(entry: ModalEntry): () => void {
        const existingIdx = stack.findIndex(m => m.id === entry.id);
        if (existingIdx !== -1) {
            // Reemplazar handler si ya existía
            stack[existingIdx] = entry;
        } else {
            if (stack.length === 0) {
                lockBodyScroll();
            }
            stack.push(entry);
        }
        notifyListeners();

        return () => {
            modalStack.remove(entry.id);
        };
    },

    /**
     * Remueve una ventana de la pila por su ID
     */
    remove(id: string): void {
        const idx = stack.findIndex(m => m.id === id);
        if (idx !== -1) {
            const removed = stack.splice(idx, 1)[0];
            if (stack.length === 0) {
                unlockBodyScroll();
            }
            notifyListeners();

            // Devolver foco al elemento disparador
            if (removed?.returnFocusElement && typeof removed.returnFocusElement.focus === 'function') {
                try {
                    removed.returnFocusElement.focus();
                } catch {
                    // Ignorar si el elemento ya no es focuseable
                }
            }
        }
    },

    /**
     * Cierra únicamente la ventana superior de la pila (LIFO)
     * Retorna true si cerró alguna, false si la pila estaba vacía
     */
    closeTop(): boolean {
        if (stack.length === 0) return false;
        const top = stack[stack.length - 1];
        if (top && typeof top.onClose === 'function') {
            top.onClose();
            return true;
        }
        return false;
    },

    /**
     * Cierra todas las ventanas de la pila (ej. al cambiar de ruta)
     */
    closeAll(): void {
        if (stack.length === 0) return;
        const copy = [...stack].reverse();
        copy.forEach(entry => {
            try {
                entry.onClose();
            } catch (err) {
                console.error('[modalStack] Error closing modal on closeAll:', err);
            }
        });
        stack = [];
        unlockBodyScroll();
        notifyListeners();
    },

    /**
     * Devuelve la cantidad de ventanas activas en la pila
     */
    getCount(): number {
        return stack.length;
    },

    /**
     * Indica si la ventana con dicho id es la superior
     */
    isTop(id: string): boolean {
        if (stack.length === 0) return false;
        return stack[stack.length - 1].id === id;
    },

    /**
     * Devuelve el índice de profundidad (0-based) de la ventana en la pila
     * 0 = primera abierta (la de más abajo), stack.length - 1 = la de arriba
     */
    getDepth(id: string): number {
        return stack.findIndex(m => m.id === id);
    },

    /**
     * Indica si hay al menos una ventana abierta en la pila
     */
    hasModals(): boolean {
        return stack.length > 0;
    },

    /**
     * Suscripción a cambios en la pila para re-renders de componentes dependientes
     */
    subscribe(fn: () => void): () => void {
        listeners.add(fn);
        return () => {
            listeners.delete(fn);
        };
    }
};
